<script setup lang="ts">
import type { NuxtLinkProps } from '#app'
import type { Crystallize } from '#shared/utils/crystallize-spec'

const props = defineProps<{
  product: ProductCard<Packet | SingleProduct>
  tag?: string
}>()

const route = useRoute()

const url = computed<NuxtLinkProps['to']>(() => ({
  path: `/produktkatalog/${props.product.slug.short}`,
  query: route.query,
}))

const title = computed(() => {
  const preference = [props.product.title, props.product.name]

  if (isPacket(props.product)) {
    // FIXME: Packets currently have a bit weird titles, so for them we want name to take precedence over title. We should fix that.
    preference.reverse()
  }

  return preference.find(truthy)
})

const bgClasses = {
  'productcard-green': 'bg-green-50 hover:bg-green-40',
  'productcard-seagreen': 'bg-seagreen-50 hover:bg-seagreen-40',
  'productcard-blue': 'bg-blue-50 hover:bg-blue-40',
  'productcard-darkblue': 'bg-blue-60 hover:bg-blue-50',
  'productcard-red': 'bg-coral-50 hover:bg-coral-40',
  'productcard-darkred': 'bg-coral-60 hover:bg-coral-50',
  'productcard-purple': 'bg-purple-50 hover:bg-purple-40',
} as const satisfies Record<Crystallize.SingleProductCardColor, string>

const tagClasses = {
  'productcard-green': 'text-green-50 bg-green-10',
  'productcard-seagreen': 'bg-seagreen-10 text-seagreen-50',
  'productcard-blue': 'text-blue-50 bg-blue-10',
  'productcard-darkblue': 'text-blue-60 bg-blue-20',
  'productcard-red': 'text-coral-50 bg-coral-10',
  'productcard-darkred': 'text-coral-60 bg-coral-20',
  'productcard-purple': 'text-purple-50 bg-purple-10',
} as const satisfies Record<Crystallize.SingleProductCardColor, string>

const tags = computed(() => [props.tag, getGradeRange(props.product)])
</script>

<template>
  <NuxtLink
    :to="url"
    :class="[product.background && bgClasses[product.background]]"
    :aria-label="`Gå til ${title}`"
    class="group z-0 flex h-full flex-col overflow-hidden rounded-[8px] no-underline transition-all duration-300 ease-in-out"
  >
    <CrystallizeImage
      class="relative max-h-32 min-h-32 w-full"
      :image="product.cover"
      img-class="w-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
    >
      <div v-if="$slots.icon" class="absolute top-s right-s">
        <slot name="icon" />
      </div>

      <div class="absolute right-[20px] bottom-[-17px] left-[20px] z-1 flex flex-wrap-reverse items-center gap-x-[9px] gap-y-[5px]  text-black">
        <template
          v-for="t in tags"
          :key="t"
        >
          <span
            v-if="t"
            class="rounded-full px-3 py-2 text-[0.6875rem] font-semibold tracking-[5%] uppercase"
            :class="[product.background && tagClasses[product.background]]"
            v-text="t"
          />
        </template>
      </div>
    </CrystallizeImage>
    <div class="relative flex flex-1 items-end justify-between bg-inherit px-[1.25rem] pt-l pb-[1.125rem] text-white">
      <div class="flex flex-col gap-2">
        <span class="text-[1.1875rem] leading-6 font-semibold" v-text="title" />
        <slot name="subtitle" />
      </div>
    </div>
  </NuxtLink>
</template>
